/* eslint-disable */
/**
 * ВЕНДОРНАЯ КОПИЯ. Здесь этот файл не правят.
 *
 * Источник — репозиторий редактора карт; тот же файл стоит в модуле
 * карты на сайте. Две копии одного алгоритма расходятся молча: маршрут
 * на сайте и в приложении начнут отличаться там, где выбор
 * неоднозначен, а по картинке этого не увидеть. Нашли ошибку или
 * нехватку — правка идёт у источника и оттуда копией в оба модуля.
 *
 * Линтер на файле выключен намеренно: копия обязана совпадать с
 * источником байт в байт, а стиль у источника свой.
 *
 * Задание, по которому файл сюда попал, — docs/map-navigation.md.
 * Упомянутого ниже docs/navigation-reference.md в этом репозитории
 * нет: он лежит у источника.
 */
/**
 * Поиск маршрута по графу навигации карты — эталонная реализация.
 *
 * Это **образец для потребителей** `nav.json`: модуля карты на сайте и
 * модуля в мобильном приложении. Файл самодостаточен — ни зависимостей,
 * ни DOM, ни импортов из редактора: его копируют в свой проект как
 * есть, а не подключают отсюда пакетом.
 *
 * Формат входных данных и правила поиска описаны в
 * `docs/navigation-reference.md` (разделы «Формат `nav.json`» и «Поиск
 * маршрута»). Здесь — их исполнение; расхождения между документом и
 * этим файлом считать ошибкой файла.
 *
 * Что важно знать, встраивая:
 *
 * - **Координаты уже в системе `viewBox` карты.** Ломаную можно
 *   класть в `<g id="route">` как есть: она поедет вместе с картой при
 *   зуме и панорамировании, потому что живёт внутри того же SVG.
 * - **Построение маршрута — операция, а не инициализация.** Роутер
 *   создаётся один раз на файл, `route()` зовётся сколько угодно раз.
 *   Режим сопровождения (посетитель идёт и пересканирует коды) не
 *   потребует переделки.
 * - **У объекта несколько адресов.** Стенд-остров привязан к трём-
 *   четырём рёбрам, и поиск идёт сразу от всех: с какой стороны
 *   подходить — результат поиска, а не решение, принятое заранее.
 */

/**
 * @typedef {{ x: number, y: number }} Point
 *
 * @typedef {Object} Anchor - Адрес объекта на ребре.
 * @property {number} side - Сторона объекта, давшая адрес.
 * @property {string} edge - Id ребра.
 * @property {number} seg - Индекс звена ломаной от узла `a`.
 * @property {number} u - Доля длины этого звена, [0..1].
 * @property {number} x - Точка проекции на ребре.
 * @property {number} y
 * @property {Point} from - Точка на самом объекте: ею начинается и
 *   заканчивается ломаная маршрута.
 * @property {number} dist - Длина хвоста от объекта до ребра.
 *
 * @typedef {Object} Target - Цель маршрута.
 * @property {string} id - Совпадает с `id` группы в SVG.
 * @property {"booth"|"poi"|"door"|"navPoint"} kind
 * @property {string} label
 * @property {boolean} [entrance] - Только у дверей: ведёт наружу.
 * @property {Anchor[]} anchors
 *
 * @typedef {Object} Route - Найденный маршрут.
 * @property {number} cost - Стоимость: длина с учётом дороговизны
 *   проходов, а не метраж. Годится для сравнения вариантов, не для
 *   показа посетителю.
 * @property {number} turns - Сколько раз маршрут сворачивает на
 *   перекрёстках. Изломы внутри коридора сюда не входят.
 * @property {Point[]} points - Вся ломаная, от объекта до объекта.
 * @property {Point[]} line - Средняя часть: проход по коридорам.
 * @property {Point[]} startTail - Хвост от объекта старта до коридора.
 * @property {Point[]} endTail - Хвост от коридора до объекта финиша.
 * @property {Target} from
 * @property {Target} to
 * @property {Anchor} fromAnchor - Какой из адресов старта выбран.
 * @property {Anchor} toAnchor
 */

/**
 * Допуск сравнения точек и длин.
 *
 * Координаты в `nav.json` округлены до сотых доли единицы карты
 * (сантиметры порядка величины), значит всё, что ближе, — одна и та
 * же точка. Точное сравнение здесь оставило бы в ломаной пары
 * точек-двойников, отличающихся в четвёртом знаке.
 */
const EPS = 0.005;

/**
 * Доплата за поворот по умолчанию, в единицах карты.
 *
 * Ноль оказался плохим значением. Разметка никогда не идеальна: узел,
 * поставленный на половину шага в сторону, делает один из равных путей
 * короче на считанные единицы — и строгое «кратчайший любой ценой»
 * разменивает на эти единицы целый поворот. На живой карте так и
 * вышло: маршрут уходил в средний проход ради выигрыша в четыре
 * сантиметра на сорокаметровом пути.
 *
 * Десять единиц — шаг сетки, то есть меньше шага человека. Такой
 * доплаты хватает, чтобы погрешность разметки не перевешивала лишний
 * поворот, и мало, чтобы отодвинуть маршрут там, где выбор настоящий.
 */
export const DEFAULT_TURN_PENALTY = 10;

/**
 * Косинус угла, до которого проход считается прямым.
 *
 * Пять градусов: коридор, повёрнутый на пару градусов, для идущего —
 * тот же коридор, а не перекрёсток с решением.
 */
const STRAIGHT = Math.cos((5 * Math.PI) / 180);

/**
 * Готовит роутер по содержимому `nav.json`.
 *
 * Вся тяжёлая работа делается здесь и один раз: длины звеньев,
 * накопительные суммы и списки смежности. Дальше поиск оперирует
 * числами и не берёт корней.
 *
 * @param {Object} nav - Разобранный `nav.json`.
 * @param {Object} [options]
 * @param {number} [options.turnPenalty=DEFAULT_TURN_PENALTY] - Доплата
 *   за каждый поворот, в единицах карты. Ноль — маршрут всегда
 *   кратчайший, чего бы это ни стоило по числу поворотов.
 * @returns {Object} Роутер.
 */
export function createRouter(nav, { turnPenalty = DEFAULT_TURN_PENALTY } = {}) {
  const nodes = new Map((nav.nodes ?? []).map(node => [node.id, node]));
  const edges = new Map();
  /** @type {Map<string, { edge: string, to: string }[]>} */
  const adjacency = new Map();

  for (const edge of nav.edges ?? []) {
    const a = nodes.get(edge.a);
    const b = nodes.get(edge.b);
    // Ребро с потерянным узлом пропускаем: пройти по нему всё равно
    // некуда, а падать на чужом файле незачем.
    if (!a || !b) continue;

    edges.set(edge.id, measure(edge, a, b));
    link(adjacency, edge.a, edge.id, edge.b);
    link(adjacency, edge.b, edge.id, edge.a);
  }

  // Рёбра нумеруются в порядке чтения — сверху вниз, слева направо.
  // Этим номером разводится последняя ничья: два зеркальных пути равны
  // и по длине, и по поворотам, и по уклонению от прямой, и выбирать
  // между ними больше нечем. Номер не несёт смысла, но он **один и тот
  // же в обе стороны** и не зависит от того, в каком порядке человек
  // рисовал коридоры, — а именно это и требовалось.
  [...edges.values()]
    .sort((first, second) => readingOrder(corner(first), corner(second))
      || (first.id < second.id ? -1 : 1))
    .forEach((edge, index) => { edge.order = index; });

  const targets = new Map((nav.targets ?? []).map(target => [target.id, target]));
  const codes = nav.codes ?? {};

  // Цели по видимому имени: у стенда это его номер, у POI и двери —
  // название. Первое вхождение выигрывает; номера уникальны (ADR-025),
  // а совпасть могут разве что названия двух дверей, и тогда любая из
  // них — одинаково верный ответ.
  const named = new Map();
  for (const target of targets.values()) {
    const key = normalize(target.label);
    if (key && !named.has(key)) named.set(key, target);
  }

  /**
   * Цели, которые показывают посетителю списком.
   *
   * Навигационных точек в списке нет: посетитель не знает, что это за
   * место, пока не отсканирует код на стене.
   *
   * @returns {Target[]}
   */
  function destinations() {
    return [...targets.values()].filter(target => target.kind !== 'navPoint');
  }

  /**
   * Двери, которые ведут снаружи внутрь: их предлагают как старт.
   *
   * @returns {Target[]}
   */
  function entrances() {
    return [...targets.values()].filter(target => target.entrance);
  }

  /**
   * Цель по коду с наклейки — ровно по коду, без догадок.
   *
   * @param {string} code
   * @returns {Target|null}
   */
  function byCode(code) {
    return targets.get(codes[code.trim()]) ?? null;
  }

  /**
   * Цель по тому, что ввёл посетитель: коду с наклейки **или** номеру
   * стенда.
   *
   * Номер нужен по трём причинам. Сканирования может не быть вовсе —
   * в первой версии модуля его и не будет, а сказать «я у стенда
   * 9V08» посетитель может уже сейчас. Поиск стенда по номеру полезен
   * и сам по себе, без всякого маршрута. И, наконец, кода у стенда
   * может просто не оказаться: коды назначают отдельным действием, и
   * карта живёт до того, как по ней проехал человек с наклейками.
   *
   * Код проверяется первым: он назначен человеком и однозначен, а
   * номер — то, что посетитель прочёл глазами.
   *
   * @param {string} query - Код или номер; регистр и пробелы не важны.
   * @returns {Target|null}
   */
  function find(query) {
    return byCode(query) ?? named.get(normalize(query)) ?? null;
  }

  /**
   * Ищет маршрут между двумя целями.
   *
   * @param {string} fromId - Id цели старта.
   * @param {string} toId - Id цели финиша.
   * @returns {Route|null} `null`, если цели неизвестны, совпадают или
   *   пути между ними нет.
   */
  function route(fromId, toId) {
    const from = targets.get(fromId);
    const to = targets.get(toId);
    if (!from || !to || from === to) return null;

    // Прямая между объектами: по ней разводятся ничьи, когда и цена, и
    // число поворотов у путей совпали. Концы берутся серединами
    // адресов, а не выбранным якорем: якорь ещё неизвестен, а прямая
    // обязана быть одной и той же для обоих направлений — иначе путь
    // обратно разойдётся с путём туда.
    const line = { a: middle(from), b: middle(to) };

    // Оба варианта считаются всегда, и берётся лучший. Соседние стенды
    // одного ряда стоят на общем ребре, и без прямого куска маршрут
    // между ними ушёл бы до ближайшего перекрёстка и обратно.
    const best = better(alongOneEdge(from, to), throughNodes(from, to, line));

    return best ? assemble(from, to, best) : null;
  }

  /**
   * Маршрут, не заходящий в узлы: обе проекции на одном ребре.
   *
   * @param {Target} from
   * @param {Target} to
   * @returns {Object|null}
   */
  function alongOneEdge(from, to) {
    let best = null;

    for (const start of from.anchors ?? []) {
      for (const finish of to.anchors ?? []) {
        const edge = edges.get(start.edge);
        if (!edge || start.edge !== finish.edge) continue;

        const at = position(edge, start);
        const until = position(edge, finish);
        const cost = start.dist + Math.abs(until - at) * edge.rate + finish.dist;

        // Поворотов нет: идём по одному ребру. Изломы коридора внутри
        // него поворотами не считаются — решать там нечего. Уклонения
        // от прямой тоже нет: короче этого куска пути не бывает.
        const candidate = {
          cost, weight: cost, turns: 0, drift: 0, mark: 0,
          start, finish, legs: [slice(edge, at, until)],
        };

        best = better(candidate, best);
      }
    }

    return best;
  }

  /**
   * Маршрут через узлы графа.
   *
   * @param {Target} from
   * @param {Target} to
   * @param {{ a: Point, b: Point }} line - Прямая между объектами.
   * @returns {Object|null}
   */
  function throughNodes(from, to, line) {
    const reached = search(from.anchors ?? [], line);

    let best = null;

    for (const finish of to.anchors ?? []) {
      const edge = edges.get(finish.edge);
      if (!edge) continue;

      const at = position(edge, finish);

      for (const [node, until] of [[edge.a, 0], [edge.b, edge.total]]) {
        for (const state of reached.get(node) ?? []) {
          // Сойти с последнего коридора к цели — тоже поворот. Но
          // только если по ребру цели действительно надо пройти: якорь
          // в самом узле сворачивать никуда не заставляет.
          const turned = Math.abs(at - until) > EPS
            && isTurn(state.heading, finish.edge, node);

          const last = Math.abs(at - until) * edge.rate + finish.dist;

          best = better({
            cost: state.cost + last,
            weight: state.weight + last + (turned ? turnPenalty : 0),
            turns: state.turns + (turned ? 1 : 0),
            drift: state.drift,
            mark: state.mark,
            state,
            finish,
            at,
            until,
          }, best);
        }
      }
    }

    return best ? { ...best, ...trace(best) } : null;
  }

  /**
   * Разворачивает найденное состояние назад, до адреса старта.
   *
   * @param {Object} best
   * @returns {{ start: Anchor, legs: Point[][] }}
   */
  function trace(best) {
    const legs = [slice(edges.get(best.finish.edge), best.until, best.at)];
    let state = best.state;

    while (state.from) {
      const edge = edges.get(state.edge);
      legs.unshift(slice(edge, endOf(edge, state.from.node), endOf(edge, state.node)));
      state = state.from;
    }

    const edge = edges.get(state.anchor.edge);
    legs.unshift(slice(edge, position(edge, state.anchor), endOf(edge, state.node)));

    return { start: state.anchor, legs };
  }

  /**
   * Поиск от всех адресов старта разом.
   *
   * **Состояние поиска — узел и ребро, по которому в него пришли**, а
   * не один узел. Иначе неизвестно направление движения, а значит и
   * то, был ли в узле поворот; без этого маршрут из равных по длине
   * выбирает случайный, и в сетке коридоров получается лесенка там,
   * где хватило бы одного поворота.
   *
   * Обход — «исправление меток»: состояние, которому нашлась цена
   * лучше, возвращается в очередь. Куча с приоритетом тут не годится
   * даром: улучшение бывает не в цене, а в числе поворотов при той же
   * цене, и уже закрытый узел приходится открывать заново. На графе
   * карты состояний сотни, и простота важнее.
   *
   * @param {Anchor[]} anchors
   * @param {{ a: Point, b: Point }} line - Прямая между объектами.
   * @returns {Map<string, Object[]>} Состояния по узлам.
   */
  function search(anchors, line) {
    /** @type {Map<string, number>} Уклонение от прямой по рёбрам. */
    const drifts = new Map();

    const driftOf = id => {
      if (!drifts.has(id)) drifts.set(id, edgeDrift(edges.get(id), line));

      return drifts.get(id);
    };

    /** @type {Map<string, Object>} Лучшее состояние на «узел|ребро». */
    const best = new Map();
    /** @type {Object[]} */
    const queue = [];

    const offer = state => {
      const key = `${state.node}|${state.edge}`;
      const known = best.get(key);
      if (known && compare(state, known) >= 0) return;

      best.set(key, state);
      queue.push(state);
    };

    for (const anchor of anchors) {
      const edge = edges.get(anchor.edge);
      if (!edge) continue;

      const at = position(edge, anchor);

      for (const [node, until] of [[edge.a, 0], [edge.b, edge.total]]) {
        offer({
          node,
          edge: anchor.edge,
          // Якорь стоит ровно в узле — идти по его ребру не пришлось,
          // и направления у пришедшего нет: он подошёл от объекта.
          heading: Math.abs(at - until) > EPS ? anchor.edge : null,
          cost: anchor.dist + Math.abs(at - until) * edge.rate,
          weight: anchor.dist + Math.abs(at - until) * edge.rate,
          turns: 0,
          drift: 0,
          mark: 0,
          from: null,
          anchor,
        });
      }
    }

    while (queue.length > 0) {
      const state = queue.shift();
      // Состояние успело устареть: его уже переписали лучшим.
      if (best.get(`${state.node}|${state.edge}`) !== state) continue;

      for (const { edge: edgeId, to } of adjacency.get(state.node) ?? []) {
        const edge = edges.get(edgeId);
        const turned = isTurn(state.heading, edgeId, state.node);

        offer({
          node: to,
          edge: edgeId,
          heading: edgeId,
          cost: state.cost + edge.total * edge.rate,
          weight: state.weight + edge.total * edge.rate + (turned ? turnPenalty : 0),
          turns: state.turns + (turned ? 1 : 0),
          drift: state.drift + driftOf(edgeId),
          mark: state.mark + edge.order,
          from: state,
        });
      }
    }

    /** @type {Map<string, Object[]>} */
    const byNode = new Map();

    for (const state of best.values()) {
      if (!byNode.has(state.node)) byNode.set(state.node, []);
      byNode.get(state.node).push(state);
    }

    return byNode;
  }

  /**
   * Поворот ли это: пришли по одному ребру, уходим по другому.
   *
   * Меряется угол между направлением прихода и направлением ухода в
   * самом узле, а не между рёбрами целиком: ребро бывает ломаным, и
   * важно, куда человек смотрит, стоя на перекрёстке.
   *
   * Возврат по тому же ребру поворотом не считается: это разворот, и
   * он всё равно никогда не выгоден.
   *
   * @param {string|null} from - Ребро прихода; `null` — направления
   *   ещё нет, человек только что подошёл от объекта.
   * @param {string} to - Ребро ухода.
   * @param {string} node
   * @returns {boolean}
   */
  function isTurn(from, to, node) {
    if (!from || from === to) return false;

    const before = direction(edges.get(from), node, 'in');
    const after = direction(edges.get(to), node, 'out');
    if (!before || !after) return false;

    return before.x * after.x + before.y * after.y < STRAIGHT;
  }

  /**
   * Собирает результат: хвосты, проход и цельная ломаная.
   *
   * @param {Target} from
   * @param {Target} to
   * @param {Object} found
   * @returns {Route}
   */
  function assemble(from, to, found) {
    const line = found.legs.reduce((all, leg) => join(all, leg), []);
    const startTail = [found.start.from, { x: found.start.x, y: found.start.y }];
    const endTail = [{ x: found.finish.x, y: found.finish.y }, found.finish.from];

    // Концы прохода берём из файла, а не из пересчёта по `u`: адрес
    // называет точку проекции прямо, и она обязана совпасть с точкой,
    // где кончается хвост.
    line[0] = startTail[1];
    line[line.length - 1] = endTail[0];

    return {
      cost: found.cost,
      turns: found.turns,
      points: join(join([...startTail], line), endTail),
      line,
      startTail,
      endTail,
      from,
      to,
      fromAnchor: found.start,
      toAnchor: found.finish,
    };
  }

  return {
    destinations,
    entrances,
    byCode,
    find,
    route,
    /** @returns {Target[]} Все цели файла, включая навигационные точки. */
    targets: () => [...targets.values()],
    /** @param {string} id @returns {Target|null} */
    target: id => targets.get(id) ?? null,
    /** @returns {Object} Копия `viewBox` карты: модуль обязан сверить. */
    viewBox: () => nav.viewBox,
    /** @returns {string} Имя файла карты, к которому относится граф. */
    map: () => nav.map,
  };
}

/**
 * Приводит введённое к виду, в котором его сравнивают: без регистра и
 * без пробелов.
 *
 * Пробелы выбрасываются, а не сжимаются: номер стенда посетитель
 * читает с таблички и набирает как придётся — «9 V 08» и «9V08» это
 * один и тот же стенд.
 *
 * @param {string} text
 * @returns {string}
 */
function normalize(text) {
  return (text ?? '').toLowerCase().replace(/\s+/g, '');
}

/**
 * Середина адресов объекта — чем он представлен для прямой
 * старт→финиш.
 *
 * Берутся точки на самом объекте (`from` у якорей): у стенда-острова
 * это даёт его середину, у двери — её проём.
 *
 * @param {Target} target
 * @returns {Point}
 */
function middle(target) {
  const points = (target.anchors ?? []).map(anchor => anchor.from);
  if (points.length === 0) return { x: 0, y: 0 };

  return {
    x: points.reduce((sum, point) => sum + point.x, 0) / points.length,
    y: points.reduce((sum, point) => sum + point.y, 0) / points.length,
  };
}

/**
 * Уклонение ребра от прямой: площадь между ними.
 *
 * Считается как сумма по звеньям — средняя удалённость концов звена,
 * умноженная на его длину. Получается «насколько далеко и насколько
 * долго» маршрут уходил в сторону: ребро в двух шагах от прямой
 * уклоняется меньше, чем такое же в двадцати.
 *
 * Это третий ключ сравнения, и точность ему нужна только чтобы
 * различать заметно разные пути: частичный проход по ребру в счёт не
 * идёт, он у всех кандидатов примерно один.
 *
 * @param {Object} edge
 * @param {{ a: Point, b: Point }} line
 * @returns {number}
 */
function edgeDrift(edge, line) {
  let total = 0;

  for (let i = 1; i < edge.points.length; i++) {
    const span = edge.cum[i] - edge.cum[i - 1];
    total += ((awayFrom(edge.points[i - 1], line) + awayFrom(edge.points[i], line)) / 2) * span;
  }

  return total;
}

/**
 * Расстояние от точки до прямой, заданной двумя точками.
 *
 * @param {Point} point
 * @param {{ a: Point, b: Point }} line
 * @returns {number}
 */
function awayFrom(point, { a, b }) {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const length = Math.hypot(dx, dy);
  if (length < EPS) return Math.hypot(point.x - a.x, point.y - a.y);

  return Math.abs((point.x - a.x) * dy - (point.y - a.y) * dx) / length;
}

/**
 * Геометрия ребра: точки ломаной, длины звеньев и накопительные суммы.
 *
 * Цена хранится долей на единицу длины — так частичный проход и целое
 * ребро считаются одной формулой. `len` и `cost` берутся из файла, а
 * длины звеньев считаются здесь: по ним режется ломаная.
 *
 * @param {Object} edge
 * @param {Point} a
 * @param {Point} b
 * @returns {Object}
 */
function measure(edge, a, b) {
  const points = [a, ...(edge.points ?? []), b].map(({ x, y }) => ({ x, y }));
  const cum = [0];

  for (let i = 1; i < points.length; i++) {
    cum.push(cum[i - 1] + Math.hypot(points[i].x - points[i - 1].x, points[i].y - points[i - 1].y));
  }

  const total = cum[cum.length - 1];
  const len = edge.len ?? total;
  const cost = edge.cost ?? len;

  return {
    id: edge.id,
    a: edge.a,
    b: edge.b,
    points,
    cum,
    total,
    // Вырожденное ребро проходом не является: цена нулевая, но и длины
    // у него нет — маршрут просто пройдёт его насквозь.
    rate: len > EPS ? cost / len : 0,
  };
}

/**
 * @param {Map<string, Object[]>} adjacency
 * @param {string} node
 * @param {string} edge
 * @param {string} to
 */
function link(adjacency, node, edge, to) {
  if (!adjacency.has(node)) adjacency.set(node, []);
  adjacency.get(node).push({ edge, to });
}

/**
 * Положение адреса на ребре — длина от узла `a`.
 *
 * @param {Object} edge
 * @param {Anchor} anchor
 * @returns {number}
 */
function position(edge, anchor) {
  const seg = Math.min(Math.max(anchor.seg, 0), edge.cum.length - 2);
  const span = edge.cum[seg + 1] - edge.cum[seg];

  return edge.cum[seg] + span * anchor.u;
}

/**
 * Положение конца ребра: у узла `a` это ноль, у `b` — вся длина.
 *
 * @param {Object} edge
 * @param {string} node
 * @returns {number}
 */
function endOf(edge, node) {
  return node === edge.a ? 0 : edge.total;
}

/**
 * Кусок ломаной между двумя положениями, в порядке движения.
 *
 * Изломы, попавшие внутрь куска, остаются: иначе на повороте коридора
 * маршрут срезал бы угол сквозь стену.
 *
 * @param {Object} edge
 * @param {number} from
 * @param {number} to
 * @returns {Point[]}
 */
function slice(edge, from, to) {
  const forward = to >= from;
  const low = forward ? from : to;
  const high = forward ? to : from;

  const middle = edge.cum
    .map((at, index) => ({ at, index }))
    .filter(({ at, index }) => index > 0 && index < edge.cum.length - 1
      && at > low + EPS && at < high - EPS)
    .map(({ index }) => edge.points[index]);

  const piece = [pointAt(edge, low), ...middle, pointAt(edge, high)];

  return forward ? piece : piece.reverse();
}

/**
 * Точка на ломаной по длине от узла `a`.
 *
 * @param {Object} edge
 * @param {number} at
 * @returns {Point}
 */
function pointAt(edge, at) {
  for (let i = 1; i < edge.cum.length; i++) {
    if (at > edge.cum[i] + EPS) continue;

    const span = edge.cum[i] - edge.cum[i - 1];
    const share = span > EPS ? (at - edge.cum[i - 1]) / span : 0;
    const start = edge.points[i - 1];
    const end = edge.points[i];

    return {
      x: start.x + (end.x - start.x) * share,
      y: start.y + (end.y - start.y) * share,
    };
  }

  return edge.points[edge.points.length - 1];
}

/**
 * Приклеивает кусок ломаной, не повторяя точку стыка.
 *
 * @param {Point[]} all
 * @param {Point[]} piece
 * @returns {Point[]}
 */
function join(all, piece) {
  const out = [...all];

  // Точки-двойники выбрасываются не только на стыке: вырожденный
  // кусок — якорь стоит ровно в узле — даёт пару одинаковых точек
  // внутри себя, и в ломаной от неё пользы нет.
  for (const point of piece) {
    const last = out[out.length - 1];
    if (last && Math.abs(last.x - point.x) < EPS && Math.abs(last.y - point.y) < EPS) continue;

    out.push(point);
  }

  return out;
}

/**
 * Лучший из двух вариантов; `null` считается бесконечно плохим.
 *
 * @param {Object|null} first
 * @param {Object|null} second
 * @returns {Object|null}
 */
function better(first, second) {
  if (!first) return second;
  if (!second) return first;

  return compare(first, second) <= 0 ? first : second;
}

/**
 * Сравнение маршрутов: вес, при равном — повороты, при равных
 * поворотах — уклонение от прямой старт→финиш.
 *
 * Вес — это стоимость пути плюс доплата за повороты. Показывают
 * человеку и отдают наружу **стоимость**: доплата придумана здесь и
 * к длине дороги отношения не имеет.
 *
 * Равной стоимость считается с точностью до сотой: числа в файле
 * округлены, и два одинаковых пути отличаются в четвёртом знаке.
 *
 * Порядок ключей именно такой: **длиной не жертвуем**. Прямизна и
 * близость к прямой разводят ничьи, а платить за прямизну длиной
 * приходится, только если это попросили через `turnPenalty` — тогда
 * доплата входит в саму стоимость.
 *
 * Третий ключ нужен потому, что в сетке коридоров ничья случается и
 * после второго: подняться можно любым из десяти проходов, и длина с
 * поворотами у всех одна. Без него ряд выбирал бы порядок рёбер в
 * файле, то есть порядок, в котором человек их рисовал.
 *
 * @param {{ weight: number, turns: number, drift: number, mark: number }} first
 * @param {{ weight: number, turns: number, drift: number, mark: number }} second
 * @returns {number}
 */
function compare(first, second) {
  if (first.weight < second.weight - EPS) return -1;
  if (first.weight > second.weight + EPS) return 1;
  if (first.turns !== second.turns) return first.turns - second.turns;
  if (Math.abs(first.drift - second.drift) > EPS) return first.drift - second.drift;

  return first.mark - second.mark;
}

/**
 * Порядок чтения: сверху вниз, слева направо.
 *
 * @param {Point} first
 * @param {Point} second
 * @returns {number}
 */
function readingOrder(first, second) {
  return first.y - second.y || first.x - second.x;
}

/**
 * Угол ребра, которым оно попадает в общий порядок: тот из концов,
 * который выше и левее.
 *
 * @param {Object} edge
 * @returns {Point}
 */
function corner(edge) {
  const head = edge.points[0];
  const tail = edge.points[edge.points.length - 1];

  return readingOrder(head, tail) <= 0 ? head : tail;
}

/**
 * Направление ребра в узле: куда смотрит идущий, входя в узел
 * (`in`) или выходя из него (`out`).
 *
 * Берётся крайнее звено ломаной, а не ребро целиком: важно, как
 * коридор подходит к перекрёстку, а не куда он в итоге ведёт.
 *
 * @param {Object} edge
 * @param {string} node
 * @param {"in"|"out"} side
 * @returns {Point|null} `null` у вырожденного звена.
 */
function direction(edge, node, side) {
  const points = edge.points;
  const last = points.length - 1;

  const [from, to] = side === 'in'
    ? (node === edge.b ? [points[last - 1], points[last]] : [points[1], points[0]])
    : (node === edge.a ? [points[0], points[1]] : [points[last], points[last - 1]]);

  const length = Math.hypot(to.x - from.x, to.y - from.y);

  return length > EPS ? { x: (to.x - from.x) / length, y: (to.y - from.y) / length } : null;
}
